// 笔记类型定义
export interface Note {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  updatedAt: Date;
  folderId: string;
  tags: string[];
  wordCount: number;
  isPinned?: boolean;
  isArchived?: boolean;
}

// 文件夹类型定义
export interface Folder {
  id: string;
  name: string;
  color: string;
  createdAt: Date;
  updatedAt: Date;
  description?: string;
}

// 标签类型定义
export interface Tag {
  id: string;
  name: string;
  color: string;
  count: number;
}

// 笔记模板类型定义
export interface NoteTemplate {
  id: string;
  name: string;
  content: string;
  tags: string[];
  icon: string;
  createdAt: Date;
}

// 笔记版本接口
export interface NoteVersion {
  id: string;
  noteId: string;
  title: string;
  content: string;
  tags?: string[];
  createdAt: Date;
  wordCount: number;
  changeDescription?: string;
}

// 用户接口
export interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  createdAt: Date;
  lastActive: Date;
}

// 应用设置类型定义
export interface AppSettings {
  theme: 'light' | 'dark';
  autoSave: boolean;
  defaultView: 'list' | 'grid' | 'card';
  sortBy: 'updatedAt' | 'createdAt' | 'title' | 'wordCount';
  sortOrder: 'asc' | 'desc';
  showWordCount: boolean;
  showReadTime: boolean;
  fontSize: 'small' | 'medium' | 'large';
  customTemplates?: NoteTemplate[];
  noteVersions?: Record<string, NoteVersion[]>;
}

// 应用状态类型定义
export interface AppState {
  notes: Note[];
  folders: Folder[];
  tags: Tag[];
  currentNote: Note | null;
  currentFolder: string | null;
  searchQuery: string;
  isLoading: boolean;
  error: string | null;
  settings: AppSettings;
  currentUser: User | null;
}

// 操作类型定义
export type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_NOTES'; payload: Note[] }
  | { type: 'ADD_NOTE'; payload: Note }
  | { type: 'UPDATE_NOTE'; payload: Note }
  | { type: 'DELETE_NOTE'; payload: string }
  | { type: 'SET_CURRENT_NOTE'; payload: Note | null }
  | { type: 'SET_FOLDERS'; payload: Folder[] }
  | { type: 'ADD_FOLDER'; payload: Omit<Folder, 'id' | 'createdAt' | 'updatedAt'> }
  | { type: 'UPDATE_FOLDER'; payload: Folder }
  | { type: 'DELETE_FOLDER'; payload: string }
  | { type: 'SET_CURRENT_FOLDER'; payload: string | null }
  | { type: 'SET_TAGS'; payload: Tag[] }
  | { type: 'ADD_TAG'; payload: Omit<Tag, 'id'> }
  | { type: 'UPDATE_TAG'; payload: Tag }
  | { type: 'DELETE_TAG'; payload: string }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<AppSettings> }
  | { type: 'RESET_STATE' }
  | { type: 'IMPORT_DATA'; payload: { notes: Note[]; folders: Folder[] } }
  | { type: 'SET_CURRENT_USER'; payload: User | null };

// 导出格式类型
export type ExportFormat = 'html' | 'markdown' | 'txt' | 'json';

// 导出选项类型
export interface ExportOptions {
  format: ExportFormat;
  includeMetadata: boolean;
  includeImages: boolean;
  preserveFormatting: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
  folders?: string[];
  tags?: string[];
}

// 搜索过滤器类型
export interface SearchFilter {
  query: string;
  folders: string[];
  tags: string[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  sortBy: AppSettings['sortBy'];
  sortOrder: AppSettings['sortOrder'];
}

// 分享类型
export type ShareType = 'wechat' | 'system' | 'copy';

// 工具函数
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const calculateWordCount = (content: string): number => {
  const plainText = content.replace(/<[^>]*>/g, '').trim();
  return plainText.length;
};

export const estimateReadTime = (wordCount: number): number => {
  return Math.max(1, Math.ceil(wordCount / 200)); // 假设每分钟200字
};

export const formatDate = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) {
    return '今天';
  } else if (days === 1) {
    return '昨天';
  } else if (days < 7) {
    return `${days}天前`;
  } else {
    return date.toLocaleDateString('zh-CN');
  }
};

export const getContentPreview = (content: string, maxLength: number = 100): string => {
  const plainText = content.replace(/<[^>]*>/g, '').trim();
  return plainText.length > maxLength ? plainText.substring(0, maxLength) + '...' : plainText;
};

// 默认设置
export const defaultSettings: AppSettings = {
  theme: 'light',
  autoSave: true,
  defaultView: 'list',
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  showWordCount: true,
  showReadTime: true,
  fontSize: 'medium',
  customTemplates: []
};